import React, { useState, useEffect } from 'react';
import { DocumentChartBarIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import axios from 'axios';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const Reports = () => {
    const [dateRange, setDateRange] = useState('Last 30 Days');
    const [isGenerating, setIsGenerating] = useState(false);
    const [stats, setStats] = useState<any>(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const userId = localStorage.getItem('ud-user') ?? '';
                const baseUrl = process.env.REACT_APP_API_URL || 'https://seller.udyamwell.in/api';
                const response = await axios.get(`${baseUrl}/dashboard/stats`, {
                    params: { userId, dateRange }
                });
                setStats(response.data);
            } catch (err) {
                console.error("Failed to load chart data:", err);
            }
        };
        fetchStats();
    }, [dateRange]);

    const handleDownload = async (reportName: string) => {
        setIsGenerating(true);
        const toastId = toast.loading(`Generating ${reportName}...`);
        try {
            const userId = localStorage.getItem('ud-user') || '';
            const baseUrl = process.env.REACT_APP_API_URL || 'https://seller.udyamwell.in/api';
            const response = await fetch(`${baseUrl}/dashboard/reports?userId=${userId}&type=${encodeURIComponent(reportName)}`);

            if (!response.ok) {
                throw new Error('Failed to generate report');
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `${reportName.replace(/\s+/g, '_')}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);

            toast.success(`${reportName} downloaded successfully!`, { id: toastId });
        } catch (err) {
            toast.error('Error generating report. Please try again.', { id: toastId });
            console.error(err);
        } finally {
            setIsGenerating(false);
        }
    };

    const reports = [
        { title: 'Seller Performance', description: 'Metrics on individual seller revenue, order volume, and fulfillment rates.', color: 'bg-blue-50 border-blue-200 text-blue-700' },
        { title: 'Sales & Revenue', description: 'Overall platform revenue trends, top-selling categories, and average order value.', color: 'bg-green-50 border-green-200 text-green-700' },
        { title: 'Inventory Status', description: 'Current stock levels across all sellers, low stock warnings, and out-of-stock items.', color: 'bg-orange-50 border-orange-200 text-orange-700' },
    ];

    return (
        <div className="p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div className="flex items-center">
                    <DocumentChartBarIcon className="w-8 h-8 text-green-600 mr-3" />
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
                        <p className="text-gray-600">Export your store data and performance metrics</p>
                    </div>
                </div>
                <div>
                    <select
                        value={dateRange}
                        onChange={e => setDateRange(e.target.value)}
                        className="bg-white border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:ring-green-500 focus:border-green-500"
                    >
                        <option>Last 7 Days</option>
                        <option>Last 30 Days</option>
                        <option>This Quarter</option>
                        <option>Year to Date</option>
                        <option>All Time</option>
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {reports.map((report, idx) => (
                    <div key={idx} className={`border rounded-xl p-6 flex flex-col ${report.color}`}>
                        <h3 className="text-lg font-bold mb-2">{report.title}</h3>
                        <p className="text-sm opacity-80 mb-6 flex-1">{report.description}</p>
                        <button
                            onClick={() => handleDownload(report.title)}
                            disabled={isGenerating}
                            className="flex items-center justify-center w-full bg-white bg-opacity-50 hover:bg-opacity-100 border border-current rounded-lg py-2 transition"
                        >
                            <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                            Download PDF
                        </button>
                    </div>
                ))}
            </div>

            {/* Live Interactive Charts */}
            {stats && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Revenue Bar Chart */}
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-6">Revenue Trend</h3>
                        {stats.monthlyRevenue && stats.monthlyRevenue.length > 0 ? (
                            <div className="h-[300px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={stats.monthlyRevenue} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} />
                                        <YAxis axisLine={false} tickLine={false} tickFormatter={(value) => `₹${value}`} />
                                        <RechartsTooltip cursor={{ fill: 'transparent' }} formatter={(value: any) => [`₹${value}`, 'Revenue']} />
                                        <Legend />
                                        <Bar dataKey="revenue" fill="#16a34a" radius={[4, 4, 0, 0]} name="Revenue (₹)" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div className="h-[300px] flex items-center justify-center text-gray-400">No revenue data available</div>
                        )}
                    </div>

                    {/* Category Pie Chart */}
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-6">Category Distribution</h3>
                        {stats.categoryPerformance && stats.categoryPerformance.length > 0 ? (
                            <div className="h-[300px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={stats.categoryPerformance}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={100}
                                            fill="#8884d8"
                                            paddingAngle={5}
                                            dataKey="value"
                                            label
                                        >
                                            {stats.categoryPerformance.map((entry: any, index: number) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div className="h-[300px] flex items-center justify-center text-gray-400">No category data available</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Reports;
